// 전투 액션 표시 타이밍(렌더 전용, docs/ACTION-ANIM-REF.md · COMBAT-FEEL 6.2). 게임 판정 틱은 바꾸지 않는다.
// 기본 공격 클립을 게임 틱에 펴는 곡선과, VFX 레인이 맞출 타격 프레임 상수를 한 곳에 둔다.
//
// 이전(선형): 선딜 [s0→hit]·후딜 [hit→s1]을 각각 등속으로 폈다. 1타는 칼날이 가장 빨리 도는 구간(칼끝 108~111m/s)이
// 판정 틱 뒤 1~6틱에 왔고 휘두름이 판정 7틱 뒤에야 끝나 "맞은 뒤에 휘두르는" 느슨한 동작이 됐다(CPU 계측 2026-10-27).
// 지금(곡선): 레퍼런스(RoR 콤보·고급 액션 릴)처럼 ① 예비동작 자세에 머물렀다가 ② 판정 틱에 가장 빠르게 닿고
// ③ 칼날 휘두름(sw1)을 판정 시작 뒤 sweepTicks 안에 끝낸 다음 ④ 뻗은 자세로 감속해 머문다(follow-through hold).

/** 판정 틱(anchor)부터 칼날 휘두름 끝(클립 sw1)까지 틱 수. 레퍼런스 한 번 베기 스미어 4~6프레임. 칼끝 궤적 리본(drawTicks)도 이 값이다. */
export const SWEEP_TICKS: readonly number[] = [4, 4, 5, 5];
/**
 * 예비동작 머묾(선딜 시작 기울기 / 선딜 평균 기울기). 작을수록 감은 자세에 오래 머물다 판정 틱에 튀어 나간다.
 * 4타(360° 회전)는 회전이 선딜 안에서 이미 시작돼 머물면 회전이 판정 직전에 몰리므로 등속(1)으로 둔다.
 */
export const COIL: readonly number[] = [0.35, 0.5, 0.35, 1];
/** 판정 틱 순간 기울기 배율(양쪽 할선 중 큰 쪽 대비). 단조 조건으로 다시 자른다 */
const CONTACT_BOOST = 1.25;
/** 후딜 표시 구간 중 휘두름 뒤 정착에 남기는 몫 */
const SETTLE_SHARE = 0.15;
/** 매듭 기울기 / 이웃 할선 상한(2.1² × 2 < 9) */
const MONO = 2.1;
/** 판정 틱부터 이 틱 동안 대검 칼날을 길이 방향으로 늘인다(스미어). 판정 틱에 최대 */
export const SMEAR_TICKS = 4;
export const SMEAR_STRETCH = 0.14;

/** 단조 3차 Hermite 곡선(Fritsch–Carlson): 매듭 4개 (0,v0) (k1,v1) (k2,v2) (k3,v3), 매듭 기울기 m0..m3. 프레임마다 값만 읽는다. */
export interface ActionCurve {
  k1: number; k2: number; k3: number;
  v0: number; v1: number; v2: number; v3: number;
  m0: number; m1: number; m2: number; m3: number;
}

export function emptyCurve(): ActionCurve {
  return { k1: 1, k2: 2, k3: 3, v0: 0, v1: 0, v2: 0, v3: 0, m0: 0, m1: 0, m2: 0, m3: 0 };
}

/**
 * 기본 공격 곡선을 out에 채운다(로딩 때). s0·hit·sw1·end는 클립 시각(초), anchor·total은 틱.
 * end = 후딜 표시 끝(칼이 바닥 위에 남는 데까지). sweep 매듭은 [anchor+1, total-1] 안, 휘두름 끝은 [hit, end] 안으로 자른다.
 */
export function buildAttackCurve(out: ActionCurve, s0: number, hit: number, sw1: number, end: number, anchor: number, total: number, sweepTicks: number, coil: number): ActionCurve {
  const k1 = Math.max(1, anchor), k3 = Math.max(k1 + 2, total);
  const k2 = Math.min(k3 - 1, Math.max(k1 + 1, k1 + sweepTicks));
  const v3 = Math.max(hit, end);
  // 휘두름 끝은 후딜 표시 끝의 SETTLE_SHARE 앞까지만: 4타처럼 칼이 곧 바닥에 닿아 표시 끝이 휘두름 끝보다 이르면
  // 남은 후딜 20틱 동안 자세가 완전히 멈춰 죽은 화면이 된다(2026-10-27 CPU 계측). 마지막 15%를 천천히 흘려 뻗은 자세에 머물게 한다.
  const v2 = Math.min(hit + (v3 - hit) * (1 - SETTLE_SHARE), Math.max(hit, sw1));
  const d0 = Math.max(0, hit - s0) / k1, d1 = (v2 - hit) / (k2 - k1), d2 = (v3 - v2) / (k3 - k2);
  // Fritsch–Carlson 단조 조건(α²+β² ≤ 9): 매듭 기울기를 이웃 할선의 2.1배로 자르면 어느 구간도 9를 넘지 않는다.
  // 한쪽 할선이 0이면 0(되감기·넘침 없음)
  const lim = (m: number, a: number, b: number) => (a <= 0 || b <= 0 ? 0 : Math.min(m, MONO * a, MONO * b));
  out.k1 = k1; out.k2 = k2; out.k3 = k3;
  out.v0 = s0; out.v1 = hit; out.v2 = v2; out.v3 = v3;
  out.m0 = d0 * Math.min(1, Math.max(0, coil));
  out.m1 = lim(Math.max(d0, d1) * CONTACT_BOOST, d0, d1);
  // 휘두름 끝: 감속해 뻗은 자세로 넘어간다(정착 구간 할선의 2.1배까지 = 끝 자세 쪽에 오래 머묾)
  out.m2 = lim(Math.max(d1, d2), d1, d2);
  out.m3 = 0;
  return out;
}

function hermite(u: number, a: number, b: number, ma: number, mb: number, h: number): number {
  const u2 = u * u, u3 = u2 * u;
  return (2 * u3 - 3 * u2 + 1) * a + (u3 - 2 * u2 + u) * h * ma + (-2 * u3 + 3 * u2) * b + (u3 - u2) * h * mb;
}

/** 곡선 값(클립 시각). t는 공격 경과 틱(보간 소수 가능). 매듭에서 정확히 v0..v3이다(판정 틱 = hit). */
export function curveTime(c: ActionCurve, t: number): number {
  if (t <= 0) return c.v0;
  if (t <= c.k1) return t === c.k1 ? c.v1 : hermite(t / c.k1, c.v0, c.v1, c.m0, c.m1, c.k1);
  if (t <= c.k2) { const h = c.k2 - c.k1; return hermite((t - c.k1) / h, c.v1, c.v2, c.m1, c.m2, h); }
  if (t < c.k3) { const h = c.k3 - c.k2; return hermite((t - c.k2) / h, c.v2, c.v3, c.m2, c.m3, h); }
  return c.v3;
}

/** 판정 틱 기준 스미어 가중치 0..1(판정 틱 1 → SMEAR_TICKS 뒤 0, 선딜 마지막 1틱에 0→1로 오른다) */
export function smearWeight(t: number, anchor: number): number {
  const d = t - anchor;
  if (d < -1 || d >= SMEAR_TICKS) return 0;
  if (d < 0) return 1 + d;
  const u = d / SMEAR_TICKS;
  return (1 - u) * (1 - u);
}
